import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Gift as GiftIcon, Mail, Check } from "lucide-react";
import { Logo } from "../components/Logo";
import { AdminFooterLink } from "../components/AdminFooterLink";
import { NavMenu } from "../components/NavMenu";
import { CardBack } from "../components/TarotCard";
import { AceHeart } from "../components/AceHeart";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

// Tarif unique du test : 10 €
const TEST_PRICE = "10 €";

export default function Gift() {
  const [payerEmail, setPayerEmail] = useState("");
  const [receiverEmail, setReceiverEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const offer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/gifts", {
        payer_email: payerEmail,
        receiver_email: receiverEmail,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen flex-col overflow-hidden bg-cream" data-testid="gift-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-cream via-cream/50 to-cream" />
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-5 sm:px-8">
        <Link to="/" data-testid="gift-home-link"><Logo size={40} /></Link>
        <NavMenu />
      </header>
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md rounded-3xl border border-gold/50 bg-white/90 p-8 text-center shadow-card backdrop-blur-md sm:p-10"
        >
          <div className="mx-auto flex h-28 items-center justify-center">
            <div className="h-28 w-20 -rotate-6"><CardBack variant={0} /></div>
            <div className="relative z-10 -mx-4 h-32 w-24"><CardBack variant={1} /></div>
            <div className="h-28 w-20 rotate-6"><CardBack variant={2} /></div>
          </div>
          <h1 className="mt-6 font-serif text-2xl font-medium text-ink sm:text-3xl">Offrez une carte mystère</h1>
          <p className="mt-3 text-sm leading-relaxed text-clay">
            Offrez le test de compatibilité APINITY à un proche célibataire : il reçoit
            par email une carte mystère avec son code cadeau pour faire le test quand il veut.
          </p>
          <p className="mt-5 font-serif text-4xl font-semibold text-ink" data-testid="gift-price">10 €</p>
          <form onSubmit={offer} className="mt-7 space-y-4 text-left">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Votre email (celui qui offre)</label>
              <input
                type="email"
                required
                value={payerEmail}
                onChange={(e) => setPayerEmail(e.target.value)}
                data-testid="gift-payer-email"
                placeholder="vous@exemple.fr"
                className="w-full rounded-xl border border-terra/20 bg-cream px-4 py-3 text-sm text-ink outline-none focus:border-terra"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Email de l'heureux·se destinataire</label>
              <input
                type="email"
                required
                value={receiverEmail}
                onChange={(e) => setReceiverEmail(e.target.value)}
                data-testid="gift-receiver-email"
                placeholder="son-amie@exemple.fr"
                className="w-full rounded-xl border border-terra/20 bg-cream px-4 py-3 text-sm text-ink outline-none focus:border-terra"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="gift-pay-button"
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-terra px-6 py-3.5 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark disabled:opacity-60"
            >
              <GiftIcon size={16} />
              {loading ? "Redirection vers le paiement…" : `Offrir le test — ${TEST_PRICE}`}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <p className="text-center text-[11px] text-stone2">Réservé aux 18 ans et plus · Paiement sécurisé par Stripe · le code cadeau part par email juste après</p>
          </form>
        </motion.div>
      </div>
      <footer className="relative z-10 border-t border-terra/10 py-8">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 text-xs font-semibold text-clay sm:px-8">
          <a href="mailto:apinity78@gmail.com" data-testid="footer-contact-link" className="transition-colors hover:text-terra">Contact</a>
          <Link to="/cgv" className="transition-colors hover:text-terra">CGV</Link>
          <Link to="/mentions-legales" className="transition-colors hover:text-terra">Mentions légales</Link>
          <Link to="/confidentialite" className="transition-colors hover:text-terra">Confidentialité</Link>
          <Link to="/pua" className="transition-colors hover:text-terra">PUA</Link>
          <Link to="/protocole-forces-ordre" className="transition-colors hover:text-terra">Protocole FDO</Link>
          <AdminFooterLink />
        </div>
      </footer>
    </div>
  );
}

export function GiftSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, code: null });
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, paid: false, code: null });
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/gifts/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, code: data.code });
          return;
        }
      } catch {}
      tries.current += 1;
      if (tries.current < 20) setTimeout(poll, 2000);
      else setState({ loading: false, paid: false, code: null });
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="grain relative flex min-h-screen flex-col overflow-hidden bg-cream" data-testid="gift-success-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-cream via-cream/50 to-cream" />
      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-5 sm:px-8">
        <Link to="/"><Logo size={40} /></Link>
        <NavMenu />
      </header>
      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md rounded-3xl border border-gold/50 bg-white/90 p-10 text-center shadow-card backdrop-blur-md"
        >
          {state.loading ? (
            <>
              <Mail size={36} className="mx-auto animate-pulse text-terra" />
              <h1 className="mt-5 font-serif text-2xl font-medium text-ink">La carte mystère se prépare…</h1>
              <p className="mt-3 text-sm text-clay">Nous confirmons votre paiement, quelques secondes suffisent.</p>
            </>
          ) : state.paid ? (
            <>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-light">
                <Check size={30} className="text-terra" />
              </div>
              <h1 className="mt-5 font-serif text-2xl font-medium text-ink" data-testid="gift-success-title">C'est envoyé !</h1>
              <p className="mt-3 text-sm leading-relaxed text-clay">
                La carte mystère vient de partir par email vers son destinataire, avec son code cadeau
                pour faire le test quand il le souhaite. <AceHeart size={14} className="mb-0.5 inline-block" />
              </p>
              {state.code && (
                <p className="mt-5 rounded-2xl border border-dashed border-gold/60 bg-parch/70 px-5 py-4 text-sm text-ink" data-testid="gift-success-code">
                  Au cas où l'email se perdrait, le code est : <strong className="font-cinzel tracking-[0.2em] text-terra">{state.code}</strong>
                </p>
              )}
              <Link to="/" data-testid="gift-success-home" className="mt-7 inline-block rounded-full bg-terra px-6 py-3 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark">
                Retour à l'accueil
              </Link>
            </>
          ) : (
            <>
              <h1 className="mt-5 font-serif text-2xl font-medium text-ink">Confirmation en attente</h1>
              <p className="mt-3 text-sm text-clay">Le paiement n'a pas encore été confirmé. S'il est bien passé, la carte mystère partira par email d'ici quelques instants.</p>
              <Link to="/" className="mt-7 inline-block rounded-full bg-terra px-6 py-3 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark">
                Retour à l'accueil
              </Link>
            </>
          )}
        </motion.div>
      </div>
      <footer className="relative z-10 border-t border-terra/10 py-8">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 text-xs font-semibold text-clay sm:px-8">
          <a href="mailto:apinity78@gmail.com" data-testid="footer-contact-link" className="transition-colors hover:text-terra">Contact</a>
          <Link to="/cgv" className="transition-colors hover:text-terra">CGV</Link>
          <Link to="/mentions-legales" className="transition-colors hover:text-terra">Mentions légales</Link>
          <Link to="/confidentialite" className="transition-colors hover:text-terra">Confidentialité</Link>
          <Link to="/pua" className="transition-colors hover:text-terra">PUA</Link>
          <Link to="/protocole-forces-ordre" className="transition-colors hover:text-terra">Protocole FDO</Link>
          <AdminFooterLink />
        </div>
      </footer>
    </div>
  );
}
